import React, { useCallback, useEffect, useRef } from "react";
import { Play, Pause, SkipBack, Clock, FlaskConical, AlertTriangle } from "lucide-react";

/* ──────────────────────────────────────────────────────────────────────────
 * Time control (brief §6): PAST → CURRENT → SIMULATION.
 *
 * WHAT THIS HONESTLY DOES, AND WHAT IT DOES NOT:
 *
 * It replays INCIDENTS backwards through time by filtering on the
 * `reportedAt` timestamps the records already carry. Scrubbing to T-40min
 * shows the incidents that had been reported by then — a real reconstruction
 * from data we hold, not a fabrication.
 *
 * It does NOT replay crowd density, zone status, alerts or resource
 * deployment. Those exist only as a single current snapshot; there is no
 * time series behind them, because there is no backend storing one.
 * Animating them would mean inventing history and showing it as observed,
 * which §6 explicitly forbids. So the crowd layer freezes during playback and
 * the panel says so, rather than quietly interpolating.
 *
 * The SIMULATION position is not a time at all — it is a different mode,
 * showing projected state. It is styled distinctly for that reason.
 * ────────────────────────────────────────────────────────────────────────── */

export const MODE = { PAST: "past", CURRENT: "current", SIMULATION: "simulation" };

/* How far back the scrubber reaches. The oldest incident in the demo set is
 * ~72 min old, so 3 hours covers the full record with headroom. */
export const WINDOW_MINUTES = 180;

export default function TimelineControl({
  lang = "en",
  mode,
  onModeChange,
  offsetMinutes,        // how far into the past, 0 = now
  onOffsetChange,
  playing,
  onPlayingChange,
  visibleIncidentCount,
  totalIncidentCount,
  hasSimulation,
}) {
  const rafRef = useRef(null);
  const lastTickRef = useRef(0);

  /* Playback walks the offset back toward zero. requestAnimationFrame rather
   * than setInterval so it pauses when the tab is hidden instead of building
   * up a backlog of catch-up frames. */
  useEffect(() => {
    if (!playing) return undefined;

    const step = (ts) => {
      if (!lastTickRef.current) lastTickRef.current = ts;
      const elapsed = ts - lastTickRef.current;
      // 1 real second = 4 simulated minutes.
      if (elapsed > 250) {
        lastTickRef.current = ts;
        onOffsetChange((prev) => {
          const next = prev - 1;
          if (next <= 0) { onPlayingChange(false); return 0; }
          return next;
        });
      }
      rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(rafRef.current);
      lastTickRef.current = 0;
    };
  }, [playing, onOffsetChange, onPlayingChange]);

  const atTime = new Date(Date.now() - offsetMinutes * 60000);
  const timeLabel = atTime.toLocaleTimeString(
    lang === "mr" ? "mr-IN-u-nu-latn" : "en-IN",
    { hour: "2-digit", minute: "2-digit" }
  );

  const restart = useCallback(() => {
    onOffsetChange(WINDOW_MINUTES);
    onPlayingChange(true);
  }, [onOffsetChange, onPlayingChange]);

  const isPast = mode === MODE.PAST;
  const isSim = mode === MODE.SIMULATION;

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-3">
        {/* Mode selector */}
        <div className="flex items-center rounded-lg border border-slate-300 overflow-hidden bg-white">
          {[
            { key: MODE.PAST,       icon: Clock,        label: lang === "mr" ? "भूतकाळ" : "Past" },
            { key: MODE.CURRENT,    icon: Play,         label: lang === "mr" ? "सद्य" : "Current" },
            { key: MODE.SIMULATION, icon: FlaskConical, label: lang === "mr" ? "सिम्युलेशन" : "Simulation" },
          ].map(({ key, icon: Icon, label }) => {
            const active = mode === key;
            const disabled = key === MODE.SIMULATION && !hasSimulation;
            return (
              <button
                key={key}
                type="button"
                disabled={disabled}
                onClick={() => onModeChange(key)}
                title={disabled ? "Run a scenario first" : undefined}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
                  active
                    ? key === MODE.SIMULATION
                      ? "bg-fuchsia-600 text-white"
                      : "bg-slate-800 text-white"
                    : disabled
                    ? "text-slate-300 cursor-not-allowed"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                <Icon size={12} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Scrubber — only meaningful in PAST */}
        {isPast && (
          <>
            <button
              type="button"
              onClick={() => onPlayingChange(!playing)}
              className="p-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause size={13} /> : <Play size={13} />}
            </button>
            <button
              type="button"
              onClick={restart}
              className="p-1.5 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
              aria-label="Restart playback"
            >
              <SkipBack size={13} />
            </button>

            <input
              type="range"
              min={0}
              max={WINDOW_MINUTES}
              value={WINDOW_MINUTES - offsetMinutes}
              onChange={(e) => {
                onPlayingChange(false);
                onOffsetChange(WINDOW_MINUTES - Number(e.target.value));
              }}
              className="flex-1 min-w-[140px] accent-slate-700"
              aria-label="Time position"
            />

            <div className="text-[11px] font-black text-slate-700 tabular-nums whitespace-nowrap">
              {offsetMinutes === 0
                ? (lang === "mr" ? "आत्ता" : "now")
                : `T−${offsetMinutes}m · ${timeLabel}`}
            </div>
          </>
        )}

        {isSim && (
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-fuchsia-700">
            <FlaskConical size={12} />
            {lang === "mr"
              ? "प्रक्षेपित स्थिती दाखवत आहे — निरीक्षण नाही"
              : "Showing projected state — not observed"}
          </div>
        )}
      </div>

      {/* The honesty line. Always visible in PAST, because the partial nature
          of the replay is the single most misleading thing about it. */}
      {isPast && (
        <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5">
          <AlertTriangle size={11} className="text-amber-700 flex-shrink-0 mt-0.5" />
          <p className="text-[10px] leading-snug text-amber-900">
            {lang === "mr" ? (
              <>
                <strong>अंशतः पुनर्रचना.</strong> फक्त घटना त्यांच्या नोंदवलेल्या वेळेनुसार
                पुन्हा दाखवल्या जातात ({visibleIncidentCount}/{totalIncidentCount} दृश्यमान).
                गर्दी घनता, झोन स्थिती व तैनाती यांचा इतिहास साठवलेला नाही — ते गोठवलेले आहेत.
              </>
            ) : (
              <>
                <strong>Partial reconstruction.</strong> Only incidents replay, filtered by
                their recorded timestamps ({visibleIncidentCount} of {totalIncidentCount} visible).
                Crowd density, zone status and deployment have no stored history, so they are
                frozen at current values — they are not being reconstructed.
              </>
            )}
          </p>
        </div>
      )}
    </div>
  );
}
